/**
 * Passkey (WebAuthn) ceremonies.
 *
 * Two kinds of tap:
 *  - **Server ceremonies** (sign-in, registration, step-up session) use SimpleWebAuthn JSON
 *    options from apps/control.
 *  - **Client-chosen challenges** (device endorsement, command step-up, enrollment approval):
 *    the challenge is SHA-256 over a domain-tagged statement (@viko/crypto), so the daemon can
 *    verify offline. UV is always required.
 *
 * The provider is swappable so MOCK mode (and tests) can run without an authenticator.
 */
import {
  type AuthenticationResponseJSON,
  browserSupportsWebAuthnAutofill,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
  type RegistrationResponseJSON,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
} from '@simplewebauthn/browser'
import {
  assertionToJSON,
  fromBase64Url,
  passkeyPrfSaltV2,
  toBase64Url,
  type WebAuthnAssertion,
} from '@viko/crypto'
import { pinLocalRegistration } from './content-trust.js'

export type { AuthenticationResponseJSON, RegistrationResponseJSON }

export interface PasskeyProvider {
  /** Assertion over a client-chosen 32-byte challenge. */
  assert(
    challenge: Uint8Array,
    opts?: { rpId?: string; allowCredentials?: string[] },
  ): Promise<WebAuthnAssertion>
  login(
    options: PublicKeyCredentialRequestOptionsJSON,
    opts?: { conditional?: boolean },
  ): Promise<AuthenticationResponseJSON>
  register(options: PublicKeyCredentialCreationOptionsJSON): Promise<RegistrationResponseJSON>
  /**
   * Registration that also evaluates PRF with the v2 salt (recovery-kit step). The PRF output is
   * stripped from `response` before it goes anywhere; only the caller sees it.
   */
  registerWithPrf?(
    options: PublicKeyCredentialCreationOptionsJSON,
  ): Promise<{ response: RegistrationResponseJSON; prf?: Uint8Array }>
  conditionalAvailable(): Promise<boolean>
  cancel(): void
  /**
   * WebAuthn PRF over a per-credential salt. Resolves undefined when the authenticator returned
   * no PRF result: capability comes from the actual extension output, never UA sniffing.
   */
  prf?(
    salts: { credentialId: string; salt: Uint8Array }[],
    /** `salt`: with no `salts` (no local pins), any discoverable passkey is evaluated with it. */
    opts?: { rpId?: string; salt?: Uint8Array },
  ): Promise<{ credentialId: string; output: Uint8Array } | undefined>
  /**
   * One ceremony for a locked browser (docs/e2e.md "Unlock via a machine"): an assertion over a
   * client-chosen challenge that ALSO evaluates PRF with `prfSalt` when the authenticator supports
   * it. The PRF output is returned only to the caller and never uploaded.
   */
  assertWithPrf?(
    challenge: Uint8Array,
    opts: { rpId?: string; allowCredentials?: string[]; prfSalt: Uint8Array },
  ): Promise<{ assertion: WebAuthnAssertion; prf?: Uint8Array }>
  /**
   * Single-ceremony sign-in (docs/e2e.md "Phone with one Face ID"): one discoverable assertion over
   * the caller's batch challenge that also evaluates PRF with a constant salt. The PRF output never
   * appears in `response` (its `clientExtensionResults` is always empty) and must not be uploaded.
   */
  loginJoin?(
    options: PublicKeyCredentialRequestOptionsJSON,
    opts: { prfSalt: Uint8Array; conditional?: boolean },
  ): Promise<{ response: AuthenticationResponseJSON; prf?: Uint8Array }>
}

export const browserPasskeys: PasskeyProvider = {
  async assert(challenge, opts = {}) {
    const cred = (await navigator.credentials.get({
      publicKey: {
        challenge: challenge as Uint8Array<ArrayBuffer>,
        ...(opts.rpId ? { rpId: opts.rpId } : {}),
        ...(opts.allowCredentials?.length
          ? {
              allowCredentials: opts.allowCredentials.map((id) => ({
                type: 'public-key' as const,
                id: fromBase64Url(id) as Uint8Array<ArrayBuffer>,
              })),
            }
          : {}),
        userVerification: 'required',
        timeout: 120_000,
      },
    })) as PublicKeyCredential | null
    if (!cred) throw new PasskeyError('cancelled', 'No passkey was used.')
    const r = cred.response as AuthenticatorAssertionResponse
    return assertionToJSON({
      rawId: cred.rawId,
      authenticatorData: r.authenticatorData,
      clientDataJSON: r.clientDataJSON,
      signature: r.signature,
      userHandle: r.userHandle,
    })
  },
  login(options, opts = {}) {
    return startAuthentication({ optionsJSON: options, useBrowserAutofill: !!opts.conditional })
  },
  async register(options) {
    const response = await startRegistration({ optionsJSON: options })
    await pinLocalRegistration(options, response, location.origin)
    return response
  },
  async registerWithPrf(options) {
    const salt = await passkeyPrfSaltV2()
    const raw = await startRegistration({
      optionsJSON: {
        ...options,
        // Passed through to navigator.credentials.create as-is (a BufferSource).
        extensions: { ...options.extensions, prf: { eval: { first: salt } } } as never,
      },
    })
    const results = raw.clientExtensionResults as { prf?: { results?: { first?: ArrayBuffer } } }
    const first = results.prf?.results?.first
    const { prf: _secret, ...rest } = raw.clientExtensionResults as Record<string, unknown>
    const response = { ...raw, clientExtensionResults: rest as typeof raw.clientExtensionResults }
    await pinLocalRegistration(options, response, location.origin)
    return {
      response,
      ...(first && first.byteLength === 32 ? { prf: new Uint8Array(first) } : {}),
    }
  },
  conditionalAvailable: () => browserSupportsWebAuthnAutofill(),
  cancel: () => {
    joinAbort?.abort()
    joinAbort = undefined
    WebAuthnAbortService.cancelCeremony()
  },
  async prf(salts, opts = {}) {
    const cred = (await navigator.credentials.get({
      publicKey: {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        ...(opts.rpId ? { rpId: opts.rpId } : {}),
        userVerification: 'required',
        timeout: 120_000,
        allowCredentials: salts.map((s) => ({
          type: 'public-key' as const,
          id: fromBase64Url(s.credentialId),
        })),
        extensions: {
          prf:
            !salts.length && opts.salt
              ? { eval: { first: opts.salt as Uint8Array<ArrayBuffer> } }
              : {
                  evalByCredential: Object.fromEntries(
                    salts.map((s) => [
                      s.credentialId,
                      { first: s.salt as Uint8Array<ArrayBuffer> },
                    ]),
                  ),
                },
        } as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential | null
    if (!cred) throw new PasskeyError('cancelled', 'No passkey was used.')
    const results = cred.getClientExtensionResults() as {
      prf?: { results?: { first?: ArrayBuffer } }
    }
    const first = results.prf?.results?.first
    if (!first || first.byteLength !== 32) return undefined
    return { credentialId: toBase64Url(new Uint8Array(cred.rawId)), output: new Uint8Array(first) }
  },
  async assertWithPrf(challenge, opts) {
    const cred = (await navigator.credentials.get({
      publicKey: {
        challenge: challenge as Uint8Array<ArrayBuffer>,
        ...(opts.rpId ? { rpId: opts.rpId } : {}),
        ...(opts.allowCredentials?.length
          ? {
              allowCredentials: opts.allowCredentials.map((id) => ({
                type: 'public-key' as const,
                id: fromBase64Url(id) as Uint8Array<ArrayBuffer>,
              })),
            }
          : {}),
        userVerification: 'required',
        timeout: 120_000,
        extensions: {
          prf: { eval: { first: opts.prfSalt as Uint8Array<ArrayBuffer> } },
        } as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential | null
    if (!cred) throw new PasskeyError('cancelled', 'No passkey was used.')
    const r = cred.response as AuthenticatorAssertionResponse
    const results = cred.getClientExtensionResults() as {
      prf?: { results?: { first?: ArrayBuffer } }
    }
    const first = results.prf?.results?.first
    return {
      assertion: assertionToJSON({
        rawId: cred.rawId,
        authenticatorData: r.authenticatorData,
        clientDataJSON: r.clientDataJSON,
        signature: r.signature,
        userHandle: r.userHandle,
      }),
      ...(first && first.byteLength === 32 ? { prf: new Uint8Array(first) } : {}),
    }
  },
  async loginJoin(options, opts) {
    const abort = new AbortController()
    joinAbort?.abort()
    joinAbort = abort
    const cred = (await navigator.credentials.get({
      ...(opts.conditional ? { mediation: 'conditional' as CredentialMediationRequirement } : {}),
      signal: abort.signal,
      publicKey: {
        challenge: fromBase64Url(options.challenge) as Uint8Array<ArrayBuffer>,
        ...(options.rpId ? { rpId: options.rpId } : {}),
        userVerification: 'required',
        timeout: options.timeout ?? 120_000,
        allowCredentials: [],
        extensions: {
          prf: { eval: { first: opts.prfSalt as Uint8Array<ArrayBuffer> } },
        } as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential | null
    if (!cred) throw new PasskeyError('cancelled', 'No passkey was used.')
    const r = cred.response as AuthenticatorAssertionResponse
    const results = cred.getClientExtensionResults() as {
      prf?: { results?: { first?: ArrayBuffer } }
    }
    const first = results.prf?.results?.first
    const a = assertionToJSON({
      rawId: cred.rawId,
      authenticatorData: r.authenticatorData,
      clientDataJSON: r.clientDataJSON,
      signature: r.signature,
      userHandle: r.userHandle,
    })
    return {
      response: {
        id: a.credentialId,
        rawId: a.credentialId,
        type: 'public-key',
        response: {
          authenticatorData: a.authenticatorData,
          clientDataJSON: a.clientDataJSON,
          signature: a.signature,
          ...(a.userHandle ? { userHandle: a.userHandle } : {}),
        },
        // Never forward extension outputs: the PRF result is a content-key secret.
        clientExtensionResults: {},
      },
      ...(first && first.byteLength === 32 ? { prf: new Uint8Array(first) } : {}),
    }
  },
}

let joinAbort: AbortController | undefined

export class PasskeyError extends Error {
  constructor(
    readonly code: 'cancelled' | 'unsupported' | 'failed',
    message: string,
  ) {
    super(message)
  }
}

let provider: PasskeyProvider = browserPasskeys

export const passkeys: PasskeyProvider = {
  assert: (c, o) => provider.assert(c, o),
  login: (o, x) => provider.login(o, x),
  register: (o) => provider.register(o),
  registerWithPrf: (o) =>
    provider.registerWithPrf
      ? provider.registerWithPrf(o)
      : provider.register(o).then((response) => ({ response })),
  conditionalAvailable: () => provider.conditionalAvailable(),
  cancel: () => provider.cancel(),
  prf: (salts, opts) => (provider.prf ? provider.prf(salts, opts) : Promise.resolve(undefined)),
  // One ceremony (unlock-via-mac): PRF in the same assertion when the provider can; else none.
  assertWithPrf: async (challenge, opts) =>
    provider.assertWithPrf
      ? provider.assertWithPrf(challenge, opts)
      : {
          assertion: await provider.assert(challenge, {
            ...(opts.rpId ? { rpId: opts.rpId } : {}),
            ...(opts.allowCredentials ? { allowCredentials: opts.allowCredentials } : {}),
          }),
        },
  loginJoin: (options, opts) => {
    if (!provider.loginJoin)
      throw new PasskeyError('unsupported', 'Single-tap sign-in unavailable.')
    return provider.loginJoin(options, opts)
  },
}

/** True when the active provider can sign in and endorse this browser in one ceremony. */
export const supportsLoginJoin = () => !!provider.loginJoin

export function setPasskeyProvider(p: PasskeyProvider) {
  provider = p
}

/** User-facing text for a failed ceremony (NotAllowedError = user dismissed or timed out). */
export function passkeyErrorMessage(e: unknown): string {
  const name = (e as { name?: string })?.name
  if (e instanceof PasskeyError) return e.message
  if (name === 'NotAllowedError' || name === 'AbortError')
    return 'The passkey request was dismissed. Try again when you are ready.'
  if (name === 'InvalidStateError') return 'This passkey is already registered here.'
  if (name === 'SecurityError') return 'Passkeys only work on the Viko domain over HTTPS.'
  return (e as Error)?.message ?? 'The passkey request failed.'
}
